import {
    faArrowLeft,
    faBookmark,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useAlert } from '../../../../AlertProvider';
import Button from '../../../../components/Button';
import ButtonLink from '../../../../components/ButtonLink';
import Divider from '../../../../components/Divider';
import Loader from '../../../../components/Loader';
import NotFound from '../../../../components/NotFound';
import Tabs from '../../../../components/Tabs';
import { alertType } from '../../../../constants/alertTypeConstant';
import {
    clientsPage,
    messagesPage,
} from '../../../../constants/pagesRoutesContants';
import { successStatus } from '../../../../constants/requestStatusContants';
import { postContactClientBookmark } from '../../../../services/bookmarkService';
import { fetchFormsResponsesByUserId } from '../../../../services/formResponseService';
import { fetchUserById } from '../../../../services/userService';
import BillsTable from '../billsTable';
import CasesTable from '../casesTable';
import ClientCaseFormsResponsesTable from '../clientCaseFormResponses/clientCaseFormResponsesTable';
import MeetingsTable from '../meetingsTable';
import styles from './style.module.scss';

function ClientDetails() {
    // ajouter une page de détails des étapes du dossier
    const { clientId = '' } = useParams();
    const alert = useAlert();

    const { data: user, isPending } = useQuery({
        queryKey: ['getUser', clientId],
        queryFn: async () => {
            if (!clientId) {
                return null;
            }
            const { data } = await fetchUserById(clientId);
            return data;
        },
    });

    const { data: formResponses } = useQuery({
        queryKey: ['formsResponses'],
        queryFn: async () => {
            try {
                const { data } = await fetchFormsResponsesByUserId(clientId);
                return data || [];
            } catch (error) {
                console.error(error);
            }
            return [];
        },
    });

    const { mutate } = useMutation({
        mutationFn: async (id: string) => {
            const { data, status } = await postContactClientBookmark(id);
            if (successStatus.includes(status)) {
                alert.show({
                    children:
                        'Vos modifications ont été enregistrées avec succès',
                    type: alertType[status],
                });
            } else {
                alert.show({
                    children:
                        'Erreur! Une erreur est survenue. Veuillez réessayer plus tard.',
                    type: alertType[status],
                });
            }
            return data;
        },
    });

    const handleBookmark = () => {
        mutate(clientId);
    };

    if (isPending) {
        return <Loader />;
    }
    if (!user) {
        return <NotFound />;
    }

    return (
        <div className={styles.body}>
            <div className={styles.header}>
                <Link to={clientsPage.path}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <h1>
                    <span className={styles.headerIcon}>
                        <FontAwesomeIcon icon={faUser} />
                    </span>
                    Client
                </h1>
            </div>
            <Divider marginBottom="20px" />
            <div className={styles.userInfoContainer}>
                <div className={styles.userProfile}>
                    <img
                        src="https://via.placeholder.com/150"
                        alt="UserProfilePicture"
                        className={styles.profilePicture}
                    />
                    <h2 className={styles.userName}>
                        {user.firstName} {user.lastName}
                    </h2>
                </div>
                <div className={styles.userDetails}>
                    <div className={styles.contactRow}>
                        <div className={styles.contactRowTitle}>
                            <strong>Adresse :</strong>
                        </div>
                        <div className={styles.contactRowData}>
                            {user.address}
                        </div>
                    </div>
                    <div className={styles.contactRow}>
                        <div className={styles.contactRowTitle}>
                            <strong>Email :</strong>
                        </div>
                        <div className={styles.contactRowData}>
                            {user.email}
                        </div>
                    </div>
                    <div className={styles.contactRow}>
                        <div className={styles.contactRowTitle}>
                            <strong>Téléphone :</strong>
                        </div>
                        <div className={styles.contactRowData}>
                            {user.phone}
                        </div>
                    </div>
                    <div className={styles.contactBtn}>
                        <ButtonLink link={messagesPage.path}>
                            Contacter
                        </ButtonLink>
                        <Button onClick={handleBookmark}>
                            <FontAwesomeIcon icon={faBookmark} />
                        </Button>
                    </div>
                </div>
            </div>
            <div className={styles.tabsContainer}>
                <Tabs
                    activeName="cases"
                    tabs={[
                        {
                            name: 'cases',
                            content: <CasesTable userId={clientId} />,
                            title: 'Dossiers',
                        },
                        {
                            name: 'meetings',
                            content: <MeetingsTable userId={clientId} />,
                            title: 'Rendez-vous',
                        },
                        {
                            name: 'forms',
                            content: (
                                <ClientCaseFormsResponsesTable
                                    formResponses={formResponses}
                                />
                            ),
                            title: 'Formulaires',
                        },
                        {
                            name: 'bills',
                            content: <BillsTable userId={clientId} />,
                            title: 'Factures',
                        },
                    ]}
                />
            </div>
        </div>
    );
}

export default ClientDetails;
